import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { transformHistoricalHoursData } from '../utils/dataTransformers';
import { setLocationHistoricalHoursSaved } from '../../../../../store/slices/dloSlice';

interface UseHistoricalHoursTableDataProps {
  historicalHoursData: any;
  historicalHoursLoading: boolean;
  historicalHoursSaved: boolean;
}

export const useHistoricalHoursTableData = ({
  historicalHoursData,
  historicalHoursLoading,
  historicalHoursSaved
}: UseHistoricalHoursTableDataProps) => {
  const dispatch = useDispatch();
  
  // State to track current historical hours data changes
  const [currentHistoricalHoursData, setCurrentHistoricalHoursData] = useState<any[]>([]);
  // Track if we've initialized the data to prevent unnecessary re-renders
  const [isDataInitialized, setIsDataInitialized] = useState(false);
  // Track if we have unsaved changes to prevent overwriting user input
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Use ref to track the previous data to avoid dependency issues
  const previousDataRef = useRef<any>(null);

  // Update local state when API data changes (only on initial load)
  useEffect(() => {
    // Only update from Redux if data is not initialized yet (initial load only)
    // and the data actually changed
    if (!isDataInitialized && historicalHoursData?.data && 
        historicalHoursData.data !== previousDataRef.current) {
      const transformedData = transformHistoricalHoursData(historicalHoursData.data);
      setCurrentHistoricalHoursData(transformedData);
      setIsDataInitialized(true);
      setHasLocalChanges(false);
      previousDataRef.current = historicalHoursData.data;
    }
  }, [historicalHoursData, isDataInitialized]);

  // Reset states when loading starts (location change, etc.)
  useEffect(() => {
    if (historicalHoursLoading && isDataInitialized) {
      setIsDataInitialized(false);
      setHasLocalChanges(false);
      // Clear current data to show loading state
      setCurrentHistoricalHoursData([]);
      previousDataRef.current = null;
    }
  }, [historicalHoursLoading, isDataInitialized]);

  // Clear local changes flag when data is saved successfully
  useEffect(() => {
    if (historicalHoursSaved && hasLocalChanges) {
      setHasLocalChanges(false);
      // Do NOT re-initialize data here - keep the current user data
    }
  }, [historicalHoursSaved]); // Remove hasLocalChanges from dependencies to avoid loop

  // Reset saved state when a new location is selected or data is refreshed
  useEffect(() => {
    if (historicalHoursLoading) {
      // When loading starts, reset the saved state
      dispatch(setLocationHistoricalHoursSaved(false));
    }
  }, [historicalHoursLoading, dispatch]);

  // Handler for when historical hours data changes
  const handleHistoricalHoursChange = useCallback((updatedData: any[]) => {
    setCurrentHistoricalHoursData(updatedData);
    setHasLocalChanges(true);
    // Reset saved state when user starts editing
    if (historicalHoursSaved) {
      dispatch(setLocationHistoricalHoursSaved(false));
    }
  }, [historicalHoursSaved, dispatch]);

  return {
    currentHistoricalHoursData,
    handleHistoricalHoursChange,
    hasLocalChanges,
    isDataInitialized
  };
};
